import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Menu,
	MenuItem,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
	Typography,
	Paper,
	useTheme
} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import * as React from "react";
import {useEffect, useState} from "react";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useMutation, useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {ArrowBack, Delete, Edit, FastForward, Info, LocalFireDepartment, MoreVert, PlayArrow, Replay} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {CustomCellRendererProps} from "ag-grid-react";
import {format} from "date-fns";
import {useDialogs} from "@toolpad/core";
import {useSnackbar} from "notistack";
import { Link } from "react-router-dom";
import {urls} from "@routes";
import { Card, CardContent, CircularProgress, Grid } from "@mui/material";
import { Close, ContentCopy, Visibility } from "@mui/icons-material";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface PaginationData {
	data: ConnectedDomain[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
	user_email: string;
}

interface NSRecordResponse {
    current_ns_records: string[];
    required_ns_records: string[];
}

interface EmailAccount {
    id: number;
    email_address: string;
    username: string;
    email_active: boolean;
    active_contacts_count: number;
}

interface Subdomain {
    id: number;
    subdomain: string;
    created_on_ts: number;
    email_accounts: EmailAccount[];
}

interface ConnectedDomain {
	id: number
	domain:string;
	user_email: string;
    user_id: number; 
	subdomains: Subdomain[];
	contacts_count: number;	
    warmup_active_days: number;	
    ns_status: string    
    ns_mismatch_detected_at: string
    last_ns_check_at: string
	active_campaigns: Array<string>
	created_on_ts: number
	setup_complete: boolean
	current_setup_stage: number
	naming_strategy: string
	aws_hosted_zone_id: string | null
	redirect_subdomains_to: string | null
	blacklist_status: string[]
	workspace_name: string
	seconds_remaining: number
	status: string
	warmup_progress_percent: number
	warmup_days_remaining: number
	workspace_id: number
	burnt: boolean
	burnt_detected_on: number | null
	burnt_via: string
	burnt_at: string
	registrar_name: string
	registrar_icon_name: string
	is_automated_import: boolean
}

interface WarmupResponse {
	warmup_data: object | null  // null indicates server was unable to fetch data.
}


export default function AdminUserConnectedDomains() {
	const {userId} = useParams();
	const { user } = useAuth();
	const navigate = useNavigate();
	const theme = useTheme();
	const dialogs = useDialogs();
	const {enqueueSnackbar} = useSnackbar();
	
	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	// const [editWarmupDateTime, setEditWarmupDateTime] = useState<any | null>(null);
	const [openEditWarmup, setOpenEditWarmup] = useState(false);
	const [warmupData, setWarmupData] = useState<null | object>(null);
	const [nsModalOpen, setNsModalOpen] = useState(false);	
	const [selectedDomain, setSelectedDomain] = useState<ConnectedDomain | null>(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [nsRecords, setNsRecords] = useState<NSRecordResponse | null>(null);
	const [exportLoading, setExportLoading] = useState(false);

	const columnCustomizerLabel = {
		domain: "Domain",
		user_email: "User Email", // new
		subdomains: "Subdomains & Emails", // new
		contacts_count: "Contacts", // new
		created_on_ts: "Added On",
		setup_complete: "Setup Complete",
		current_setup_stage: "Setup Stage",
		status: "Status",
		naming_strategy: "Naming Strategy",
		aws_hosted_zone_id: "AWS Hosted Zone ID",
		blacklist_status: "Blacklist Status",
		workspace_name: "Workspace Name",
		redirect_subdomains_to: "Domain Redirect",
		warmup: "Domain Warmup",
		active_campaigns: "Active Campaigns",
		ns_status: "NS Status",
		ns_mismatch_detected_at: "NS Issue Since",
		last_ns_check_at: "Last NS Check",
		warmup_progress: "Warmup Progress",
		warmup_days_remaining: "Warmup Days Left",
		nameservers: "Nameservers",
		burnt: "Burnt",
		burnt_detected_on: "Burnt On",
		burnt_via: "Burnt Via",
		burnt_at: "Burnt At",
		registrar_name: "Domain Registrar Name",
		registrar_icon_name: "Registrar Icon",
		is_automated_import: "Import Type",
	};

	// Query to fetch all connected domains.
	const adminUserConnectedDomainsQuery = useQuery({
		queryKey: [
			"adminUserConnectedDomainsQuery",
			userId,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/get-user-connected-domains/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (adminUserConnectedDomainsQuery.data?.data) {
			const response = adminUserConnectedDomainsQuery.data.data;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
				user_email: response.user_email,
			});

			const domains = response.data.map(
				(value: ConnectedDomain) => value.domain
			);

			fetchWarmupDataMutation.mutate(domains);
		}
	}, [adminUserConnectedDomainsQuery.data]);

	// Mutation for deleting domain.
	const deleteDomainMutation = useMutation({
		mutationKey: ["adminDeleteDomainMutation"],
		mutationFn: (domain_id: string) => authenticateAndPostData("/admin/delete-domain/", {
			id: domain_id
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			enqueueSnackbar("Domain is being deleted. It might take a few seconds. Please refresh and check later.", {
				variant: "success",
			});
			// adminUserConnectedDomainsQuery.refetch().then();
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	})

	// Mutation to fetch warmup data
	const fetchWarmupDataMutation = useMutation({
		mutationKey: ["adminEmailAccountsWarmupData"],
		mutationFn: (domains: string[]) => authenticateAndPostData("/admin/user-connected-domains/get-warmup-data/", {
			domains: domains,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			let data: WarmupResponse = response.data;
			setWarmupData(data.warmup_data);
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const fetchNSRecordMutation = useMutation({
		mutationKey: ["adminFetchNSRecordData"],
		mutationFn: (data: { workspace_id: number; domain: string }) => 			
			authenticateAndPostData(
				`/admin/admin-domain-ns-record/?workspace_id=${data.workspace_id}&domain=${data.domain}`, 
				{}
			),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			const data: NSRecordResponse = response.data;
			setNsRecords(data);
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to fetch NS records", {
				variant: "error",
			});
			setNsModalOpen(false);
		}
	});

	const retryPostalSetupMutation = useMutation({
		mutationKey: ["retryPostalSetup"],
		mutationFn: (msub_id: number) =>
			authenticateAndPostData("/admin/retry-postal-setup/", {
				msub_id: msub_id,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			adminUserConnectedDomainsQuery.refetch().then()
			enqueueSnackbar("Retry in progress. Your domain setup is in progress again. It should take around 3-5 mins.", {
				variant: "success",
			});			
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to Retry Postal setup", {
				variant: "error",
			});			
		},
	});

	const unmarkBurntMutation = useMutation({
		mutationKey: ["unmarkDomainBurnt"],
		mutationFn: (domainId: number) =>
			authenticateAndPostData("/admin/domain/unmark-burnt/", {
				id: domainId,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			adminUserConnectedDomainsQuery.refetch().then()
			enqueueSnackbar("Domain unmarked as burnt. Warmup will resume, campaigns stay paused.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to unmark domain as burnt", {
				variant: "error",
			});
		},
	});


	useEffect(() => {
		if (fetchWarmupDataMutation.data) {
			setWarmupData(fetchWarmupDataMutation.data.data["warmup_data"]);
		}
	}, [fetchWarmupDataMutation.data]);

	// Mutation to register domain with warmup system.
	const registerWarmupDomainMutation = useMutation({
		mutationKey: ["adminEmailAccountsRegisterWarmupDomain"],
		mutationFn: (emailAccountId: number) => authenticateAndPostData("/admin/warmup/register-domain/", {
			id: emailAccountId,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				adminUserConnectedDomainsQuery.refetch().then()
				enqueueSnackbar("Domain warmup enabled", {
					variant: "success",
				});
			} else {
				console.error("Page data not set.")
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to resume warmup.
	const resumeWarmupMutation = useMutation({
		mutationKey: ["adminEmailAccountsResumeWarmup"],
		mutationFn: (emailAccountId: number) => authenticateAndPostData("/admin/warmup/resume/", {
			id: emailAccountId,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				const domains: string[] = pageData?.data?.map((value: ConnectedDomain) => value.domain) || [];
				fetchWarmupDataMutation.mutate(domains);
			} else {
				console.error("Page data not set.")
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to pause warmup.
	const pauseWarmupMutation = useMutation({
		mutationKey: ["adminEmailAccountsPauseWarmup"],
		mutationFn: (emailAccountId: number) => authenticateAndPostData("/admin/warmup/pause/", {
			id: emailAccountId,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				const domains: string[] = pageData?.data?.map((value: ConnectedDomain) => value.domain) || [];
				fetchWarmupDataMutation.mutate(domains);
			} else {
				console.error("Page data not set.")
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to edit warmup limit for domain.
	const editDomainWarmupLimitMutation = useMutation({
		mutationKey: ["adminEmailAccountEditDomainWarmupLimit"],
		mutationFn: (data: {
			emailAccountId: number,
			newLimit: number
		}) => authenticateAndPostData("/admin/warmup/edit-domain-limit/", {
			id: data.emailAccountId,
			new_limit: data.newLimit,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				// const domains: string[] = pageData.connected_domains.map(value => value["domain"])
				// fetchWarmupDataMutation.mutate(domains);
				adminUserConnectedDomainsQuery.refetch().then();
				enqueueSnackbar("Domain warmup limit updated successfully!", {
					variant: "success",
				});
			} else {
				console.error("Page data not set.")
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Muatation to skip initial warmup period.
	const skipWarmupMutation = useMutation({
		mutationKey: ["adminSkipWarmup"],
		mutationFn: (domainId: number) => authenticateAndPostData("/admin/warmup/skip/", {
			domain_id: domainId
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			adminUserConnectedDomainsQuery.refetch().then();
			enqueueSnackbar("Domain warmup limit updated successfully!", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	})

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

    const handleOpenNameserverDialog = (rowData: ConnectedDomain) => {
		setSelectedDomain(rowData);
		setNsRecords(null); // Reset previous state data
		setNsModalOpen(true);
		
		// Fire the mutation request with the row parameters
		fetchNSRecordMutation.mutate({
			workspace_id: rowData.workspace_id,
			domain: rowData.domain
		});
	};

	const copyToClipboard = (text: string, successMessage: string) => {
		navigator.clipboard.writeText(text).then(() => {
			enqueueSnackbar(successMessage, { variant: "success", autoHideDuration: 2000 });
		}).catch(() => {
			enqueueSnackbar("Failed to copy to clipboard", { variant: "error" });
		});
	};

	const handleOpenDialog = (domain: ConnectedDomain) => {
        setSelectedDomain(domain);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedDomain(null);
    };

	function timestampFormatter(params: ValueFormatterParams) {
		// Return a fallback dash if value is null, undefined, 0, or an empty string
		if (!params.value) {
			return "---"; 
		}
		return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
	}

	function setupCompleteCellRenderer(props: CustomCellRendererProps) {
		return props.value ? <span style={{color: theme.palette.success.main}}>Yes</span> : <span>No</span>
	}

	const blackListStatus = (params: CustomCellRendererProps) => {
		const value = params.value;
		if (!value || value === "Healthy") {
			return (
				<Chip
					size="small"
					color="success"
					label="Healthy"
					sx={{fontSize: "0.75rem", height: 24}}
				/>
			);
		}

		if (value === "Incomplete") {
			return (
				<Chip
					size="small"
					color="default"
					label="Incomplete"
					sx={{fontSize: "0.75rem", height: 24}}
				/>
			);
		}

		if (value === "Unknown Error") {
			return (
				<Chip
					size="small"
					color="error"
					label="Unknown Error"
					sx={{fontSize: "0.75rem", height: 24}}
				/>
			);
		}

		const sources = typeof value === "string" ? value.split(",") : [];

		return (
			<Box sx={{mt: 1}}>
				<Stack spacing={0.5} sx={{maxWidth: 180}}>
					{sources.slice(0, 3).map((source: string, idx: number) => (
						<Tooltip title={source.trim()} key={idx}>
							<Chip
								size="small"
								color="warning"
								label={source.trim()}
								sx={{
									maxWidth: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									fontSize: "0.75rem",
									height: 24,
								}}
							/>
						</Tooltip>
					))}
					{sources.length > 3 && (
						<Chip
							size="small"
							color="warning"
							label={`+${sources.length - 3} more`}
							sx={{fontSize: "0.75rem", height: 24}}
						/>
					)}
				</Stack>
			</Box>
		);
	};

	const BurntStatusRenderer = (cellProps: CustomCellRendererProps) => {
		return cellProps.value ? (
			<Chip label="Burnt" color="error" size="small" />
		) : (
			<span style={{color: "#999"}}>---</span>
		);
	};

	const ActionsButton = (props: CustomCellRendererProps) => {
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const open = Boolean(anchorEl);

		const handleClick = (event: any) => {
			setAnchorEl(event.currentTarget);
		};

		const handleClose = () => {
			setAnchorEl(null);
		};

		const handleResumeSetup = () => {
			const subdomain = props.data["domain"];
			navigate(
				`${urls["adminConnectDomain"]
					.replace(':userId', userId ?? "")
					.replace(':workspaceId', props.data["workspace_id"] ?? "")}?subdomain=${subdomain}`
			);
			handleClose();
		};

		const handleRetrySetup = () => {
			retryPostalSetupMutation.mutate(props.data["id"]);
			handleClose();
		};

		const handleDelete = async () => {
			if (props.data["active_campaigns"].length > 0) {
				const deleteConfirmed = await dialogs.confirm(
					(
						<DialogContentText sx={{whiteSpace: "pre-line"}}>
							This will permanently remove <b>{props.data["domain"]}</b> from DeliverymanAI.
							This domain is currently being used in the following active campaigns:
							<br/><br/>
							{props.data["active_campaigns"].join("\n")}
							<br/><br/>
							Proceed?
						</DialogContentText>
					),
					{
						title: `Confirm deleting ${props.data["domain"]}?`,
						okText: "Proceed",
					}
				);

				if (deleteConfirmed) {
					deleteDomainMutation.mutate(props.data["id"]);
				}
			} else {
				const deleteConfirmed = await dialogs.confirm(
					(
						<DialogContentText sx={{whiteSpace: "pre-line"}}>
							This will permanently remove <b>{props.data["domain"]}</b> from DeliverymanAI. Proceed?
						</DialogContentText>
					),
					{
						title: `Confirm deleting ${props.data["domain"]}?`,
						okText: "Proceed",
					}
				);

				if (deleteConfirmed) {
					deleteDomainMutation.mutate(props.data["id"]);
				}
			}

			handleClose();
		};

		const handleSkipWarmup = async () => {
			const skipConfirmed = await dialogs.confirm(
				(
					<DialogContentText sx={{whiteSpace: "pre-line"}}>
						This will skip warmup and move <b>{props.data["domain"]}</b> to "active" status.
						We'll lose tracking data for remaining warmup days and can only restart the process for full 14 days.
						<br/><br/>
						Proceed?
					</DialogContentText>
				),
				{
					title: `Confirm deleting ${props.data["domain"]}?`,
					okText: "Proceed",
				}
			);

			if (skipConfirmed) {
				skipWarmupMutation.mutate(props.data["id"]);
			}
		}

		return (
			<>
				<IconButton
					aria-label="more"
					aria-controls={open ? "long-menu" : undefined}
					aria-expanded={open ? "true" : undefined}
					aria-haspopup="true"
					onClick={handleClick}
				>
					<MoreVert/>
				</IconButton>
				<Menu
					id="long-menu"
					MenuListProps={{
						"aria-labelledby": "long-button",
					}}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
				>
					{/*<MenuItem*/}
					{/*	onClick={() => {*/}
					{/*		// setEditWarmupDateTime(props.data);*/}
					{/*		setOpenEditWarmup(true);*/}
					{/*	}}*/}
					{/*>*/}
					{/*	<Edit color={"warning"} sx={{mr: 1}}/>*/}
					{/*	<Typography variant="subtitle2">Edit Warmup</Typography>*/}
					{/*</MenuItem>*/}

					<MenuItem onClick={async () => {
						const limitInput: string | null = await dialogs.prompt(
							"Enter new limit (needs to be multiple of current daily increment value)"
						)
						if (limitInput !== null) {
							const limitInputAsNumber = parseInt(limitInput);
							if (isNaN(limitInputAsNumber)) {
								enqueueSnackbar("Please enter a valid number", {
									variant: "error",
								});
								return
							}

							// Make request.
							editDomainWarmupLimitMutation.mutate({
								emailAccountId: props.data["id"],
								newLimit: limitInputAsNumber,
							});
						}
					}}>
						<Edit color={"warning"} sx={{mr: 1}}/>
						<Typography variant="subtitle2">Edit Warmup</Typography>
					</MenuItem>
					{/* Skip initial 14 day warmup period */}
					{props.data["status"] === "warmup" && <MenuItem
              onClick={handleSkipWarmup}
              disabled={skipWarmupMutation.isPending}>
              <FastForward color="success" sx={{mr: 1}}/>
              <Typography variant="subtitle2">Skip Warmup</Typography>
          </MenuItem>}
					{!props.data["setup_complete"] && (
						<MenuItem onClick={handleResumeSetup}>
							<PlayArrow color="success" sx={{mr: 1}}/>
							<Typography variant="subtitle2">Resume Setup</Typography>
						</MenuItem>
					)}					
					{props.data["status"] === "postal_error" && (
						<MenuItem 
							onClick={handleRetrySetup}
							disabled={retryPostalSetupMutation.isPending}
						>
							<Replay color={retryPostalSetupMutation.isPending ? "disabled" : "primary"} sx={{ mr: 1 }} />
							<Typography variant="subtitle2">
								{retryPostalSetupMutation.isPending ? "Retrying..." : "Retry Setup"}
							</Typography>
						</MenuItem>
					)}					
					{props.data["burnt"] && (
						<MenuItem
							onClick={() => {
								handleClose();
								unmarkBurntMutation.mutate(props.data["id"]);
							}}
							disabled={unmarkBurntMutation.isPending}
						>
							<LocalFireDepartment color={unmarkBurntMutation.isPending ? "disabled" : "warning"} sx={{mr: 1}}/>
							<Typography variant="subtitle2">
								{unmarkBurntMutation.isPending ? "Unmarking..." : "Unmark Burnt"}
							</Typography>
						</MenuItem>
					)}
					<MenuItem onClick={handleDelete}>
						<Delete color="error" sx={{mr: 1}}/>
						<Typography variant="subtitle2">Delete Domain</Typography>
					</MenuItem>
				</Menu>
			</>
		);
	};

	const NameserverDialog = () => {
		// If you renamed the variable to selectedNsDomain in the previous step, update this reference accordingly
		if (!selectedDomain) return null;

		const isLoading = fetchNSRecordMutation.isPending;
		const requiredRecords = nsRecords?.required_ns_records || [];

		return (
			<Dialog
				open={nsModalOpen}
				onClose={() => setNsModalOpen(false)}
				maxWidth="sm" // Changed from 'md' to 'sm' for a better single-column fit
				fullWidth
				PaperProps={{ sx: { borderRadius: 3 } }}
			>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							Required Nameservers for {selectedDomain.domain}
						</Typography>
						<IconButton onClick={() => setNsModalOpen(false)} size="small">
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent dividers sx={{ minHeight: 180, display: "flex", flexDirection: "column", justifyContent: "center" }}>
					{isLoading ? (
						<Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
							<CircularProgress size={40} />
							<Typography variant="body2" color="text.secondary">
								Fetching configuration records...
							</Typography>
						</Box>
					) : nsRecords ? (
						<Box sx={{ width: "100%", py: 1 }}>
							<Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
								Required AWS Records ({requiredRecords.length})
							</Typography>
							
							<Stack spacing={1.5}>
								{requiredRecords.length > 0 ? (
									requiredRecords.map((ns, idx) => (
										<Card key={idx} variant="outlined" sx={{ 
												bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : '#fafafa',
												borderRadius: 2 
											}}>
											<CardContent sx={{ 
												display: "flex", 
												justifyContent: "space-between", 
												alignItems: "center", 
												p: "12px !important" 
											}}>
												<Typography variant="body2" component="code" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
													{ns}
												</Typography>
												<Tooltip title="Copy Record">
													<IconButton 
														size="small" 
														onClick={() => copyToClipboard(ns, `Copied: ${ns}`)}
														color="primary"
													>
														<ContentCopy fontSize="inherit" />
													</IconButton>
												</Tooltip>
											</CardContent>
										</Card>
									))
								) : (
									<Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", py: 1 }}>
										No records found.
									</Typography>
								)}
							</Stack>
						</Box>
					) : (
						<Alert severity="error" sx={{ width: "100%" }}>
							Failed to fetch nameserver details. Please close and try again.
						</Alert>
					)}
				</DialogContent>
				
				<DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
					{nsRecords && requiredRecords.length > 0 ? (
						<Button
							variant="outlined"
							startIcon={<ContentCopy />}
							onClick={() => copyToClipboard(requiredRecords.join("\n"), "Copied all required entries!")}
							sx={{ borderRadius: 2 }}
						>
							Copy All Records
						</Button>
					) : <Box />}
					
					<Button variant="contained" onClick={() => setNsModalOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
						Close View
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	const WarmupCell = (customProps: CustomCellRendererProps) => {
		if (warmupData === null) {
			return <>---</>

		} else {
			// Fetch warmup status for this domain.
			const domain = customProps.data["domain"];
			const isWarmupRunning = warmupData[domain]
			// const isWarmupRunning = domainDetails?.warmup_running ?? null;

			if (fetchWarmupDataMutation.isPending || adminUserConnectedDomainsQuery.isFetching) {
				return (
					<>Loading</>
				)
			}

			if (isWarmupRunning === null || isWarmupRunning === undefined) {
				const status = customProps.data["status"];
				const isSetupComplete = customProps.data["setup_complete"];

				if (["active", "warmup", "activating"].includes(status) && isSetupComplete) {
					return (
						<Button
							variant={"outlined"}
							color={"primary"}
							size={"small"}
							disabled={
								resumeWarmupMutation.isPending ||
								pauseWarmupMutation.isPending ||
								registerWarmupDomainMutation.isPending
							}
							onClick={() => {
								registerWarmupDomainMutation.mutate(customProps.data["id"]);
							}}
						>
							Enable
						</Button>
					);
				} else {
					return <>---</>;
				}
			} else {
				// Render switch based on the actual 'warmup_running' boolean
				return (
					<Switch
						checked={isWarmupRunning}
						disabled={
							resumeWarmupMutation.isPending ||
							pauseWarmupMutation.isPending ||
							registerWarmupDomainMutation.isPending
						}
						onChange={(_event, checked) => {
							if (checked) {
								resumeWarmupMutation.mutate(customProps.data["id"]);
							} else {
								pauseWarmupMutation.mutate(customProps.data["id"]);
							}
						}}
					/>
				);
			}
		}
	}

	const WarmupProgressCell = (customProps: CustomCellRendererProps) => {
		if (customProps.data["warmup_progress_percent"] < 100) {
			return (
				<Chip size={"small"} color={"warning"} label={
					<Stack direction={"row"} justifyContent={"center"} alignItems={"center"} spacing={1}>
						<Tooltip title={`${customProps.data["warmup_days_remaining"]} days remaining`}>
							<Info sx={{fontSize: "1em", m: 0}}/>
						</Tooltip>
						<div>{customProps.data["warmup_progress_percent"]}% done</div>
					</Stack>
				}/>
			)
		} else {
			return (
				<Chip size={"small"} color={"success"} label={`${customProps.data["warmup_progress_percent"]}% done`}/>
			)
		}
	}


	const SubdomainsRenderer = (cellProps: CustomCellRendererProps) => {
		const subdomainsCount = cellProps.data.subdomains?.length || 0;
		const emailAccountsCount = cellProps.data.subdomains?.reduce(
			(acc: number, sub: Subdomain) => acc + (sub.email_accounts?.length || 0),
			0
		) || 0;

		return (
			<Box display="flex" alignItems="center" gap={1}>
				<Chip label={`${subdomainsCount} subdomain(s)`} size="small" variant="outlined" />
				<Chip label={`${emailAccountsCount} email(s)`} size="small" variant="outlined" />
				<IconButton
					size="small"
					onClick={() => handleOpenDialog(cellProps.data)}
					title="View details"
				>
					<Visibility fontSize="small" />
				</IconButton>
			</Box>
		);
	};

	const UserEmailLink = (cellProps: CustomCellRendererProps) => {
		return (
			<Link
				to={urls["adminUserDetails"].replace(":userId", cellProps.data["user_id"])}
				style={{ color: theme.palette.text.primary }}
			>
				{cellProps.value}
			</Link>
		);
	};

	const ActiveCampaignsRenderer = (cellProps: CustomCellRendererProps) => {
		const campaigns = cellProps.value as string[];
		if (!campaigns || campaigns.length === 0) {
			return <Typography variant="body2">---</Typography>;
		}
		return (
			<Box display="flex" gap={0.5} flexWrap="wrap">
				{campaigns.slice(0, 2).map((campaign, idx) => (
					<Chip key={idx} label={campaign} size="small" variant="outlined" />
				))}
				{campaigns.length > 2 && (
					<Chip label={`+${campaigns.length - 2} more`} size="small" variant="outlined" />
				)}
			</Box>
		);
	};

	const NsStatusRenderer = (cellProps: CustomCellRendererProps) => {
		const status = cellProps.value as string | null;

		if (!status) {
			return <span style={{ color: "#999" }}>---</span>;
		}

		let label = "";
		let color: "success" | "error" | "warning" | "default" = "default";

		if (status === "healthy") {
			label = "Healthy";
			color = "success";
		} else if (status === "mismatch") {
			label = "Mismatch";
			color = "error";
		}

		return <Chip label={label} color={color} size="small" />;
	};

	const DomainRedirectCell = (cellProps: CustomCellRendererProps) => {
		return (
			<>
				{cellProps.value || "---"}{"  "}<DomainRedirectionDialog managedSubdomain={cellProps.data["domain"]}
																																 redirect_to={cellProps.data["redirect_subdomains_to"]}
																																 disabled={cellProps.data["status"] === "deleting"}
																																 workspaceId={cellProps.data["workspace_id"]}/>
			</>
		)
	}

	const SubdomainsDialog = () => {
		if (!selectedDomain) return null;

		return (
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">
							Subdomains & Email Accounts for {selectedDomain.domain}
						</Typography>
						<IconButton onClick={handleCloseDialog} size="small">
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					{selectedDomain.subdomains && selectedDomain.subdomains.length > 0 ? (
						selectedDomain.subdomains.map((subdomain) => (
							<Box key={subdomain.id} sx={{ mb: 3 }}>
								<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
									Subdomain: {subdomain.subdomain}
								</Typography>
								<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
									Created: {format(new Date(subdomain.created_on_ts), "do MMM y, h:mm a")}
								</Typography>
								{subdomain.email_accounts && subdomain.email_accounts.length > 0 ? (
									<TableContainer component={Paper} variant="outlined">
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell><strong>Email Address</strong></TableCell>
													<TableCell><strong>Username</strong></TableCell>
													<TableCell><strong>Status</strong></TableCell>
													<TableCell><strong>Active Contacts</strong></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{subdomain.email_accounts.map((email) => (
													<TableRow key={email.id}>
														<TableCell>{email.email_address}</TableCell>
														<TableCell>{email.username}</TableCell>
														<TableCell>
															<Chip
																label={email.email_active ? "Active" : "Inactive"}
																color={email.email_active ? "success" : "default"}
																size="small"
															/>
														</TableCell>
														<TableCell>{email.active_contacts_count}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								) : (
									<Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
										No email accounts
									</Typography>
								)}
								<Divider sx={{ mt: 2 }} />
							</Box>
						))
					) : (
						<Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
							No subdomains available
						</Typography>
					)}
				</DialogContent>
			</Dialog>
		);
	};
	
	// Columns for connected domains table.
	const columnDefs: ColDef[] = [
		{field: "domain", headerName: "Domain", minWidth: 200},
		{
            field: "user_email",
            headerName: "User Email",
            cellRenderer: UserEmailLink,
            minWidth: 200,
        },
		{
			field: "created_on_ts",
			headerName: "Added On",
			valueFormatter: timestampFormatter,
			comparator: (valueA, valueB, nodeA, nodeB) => {
				return nodeA.data["created_on_ts"] > nodeB.data["created_on_ts"] ? 1 : -1;
			},
			minWidth: 180,
		},
		{
            field: "subdomains",
            headerName: "Subdomains & Emails",
            cellRenderer: SubdomainsRenderer,
            minWidth: 250,
            sortable: false,
        },
		{
			field: "setup_complete",
			headerName: "Setup Complete",
			cellRenderer: setupCompleteCellRenderer,
			getQuickFilterText: params => params.value ? "Yes" : "No",
			comparator: (valueA, valueB) => {
				const timeA = valueA ? new Date(valueA).getTime() : 0;
				const timeB = valueB ? new Date(valueB).getTime() : 0;
				return timeA - timeB;
			},
		},
		{field: "current_setup_stage", headerName: "Setup Stage", minWidth: 120},
		{
			field: "nameservers",
            headerName: "Nameservers",
            flex: 2,
            cellRenderer: (params: CustomCellRendererProps<ConnectedDomain>) => {
                if (!params.data) return <span style={{ color: "text.secondary" }}>—</span>;
                
                return (
                    <span
                        onClick={() => handleOpenNameserverDialog(params.data!)}
                        style={{
                            cursor: "pointer",
                            color: "#1976d2",
                            fontWeight: 500,
                            textDecoration: "underline"
                        }}
                    >
                        Check NS Records
                    </span>
                );
            },
			minWidth: 150,
        },
		{
            field: "active_campaigns",
            headerName: "Active Campaigns",
            cellRenderer: ActiveCampaignsRenderer,
            minWidth: 200,
        },
		{
            field: "contacts_count",
            headerName: "Contacts",
            minWidth: 100,
        },
		{
            field: "warmup_days_remaining",
            headerName: "Warmup Days Left",
            minWidth: 160,
        },
		{field: "status", headerName: "Status", minWidth: 150,},		
		{field: "naming_strategy", headerName: "Naming Strategy", minWidth: 150},
		{field: "aws_hosted_zone_id", headerName: "AWS Hosted Zone ID", minWidth: 200,},
		{field: "blacklist_status", headerName: "Blacklist Status", cellRenderer: blackListStatus, minWidth: 150,},
		{field: "workspace_name", headerName: "Workspace Name", minWidth: 150,},
		{field: "redirect_subdomains_to", headerName: "Domain Redirect", cellRenderer: DomainRedirectCell,
			sortable: false,
			minWidth: 200,},
		{
			headerName: "Domain Warmup",
			colId: "warmup",
			cellRenderer: WarmupCell,
			sortable: false,
			minWidth: 100,
		},
		{
			headerName: "Warmup Progress",
			colId: "warmup_progress",
			cellRenderer: WarmupProgressCell,
			sortable: false,
			minWidth: 150,
		},
		{
            field: "ns_status",
            headerName: "NS Status",
            cellRenderer: NsStatusRenderer,
            minWidth: 140,
        },
		{
            field: "ns_mismatch_detected_at",
            headerName: "NS Issue Since",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
        {
            field: "last_ns_check_at",
            headerName: "Last NS Check",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
		{
			field: "burnt",
			headerName: "Burnt",
			cellRenderer: BurntStatusRenderer,
			minWidth: 120,
		},
		{
			field: "burnt_detected_on",
			headerName: "Burnt On",
			valueFormatter: timestampFormatter,
			minWidth: 180,
		},
		{field: "burnt_via", headerName: "Burnt Via", minWidth: 200,},
		{field: "burnt_at", headerName: "Burnt At", minWidth: 120,},
		{
            field: "registrar_name",
            headerName: "Domain Registrar Name",
            valueGetter: (params) => params.data?.registrar_name ? params.data.registrar_name : "Manual",
            minWidth: 140,
        },
        {
            field: "registrar_icon_name",
            headerName: "Registrar Icon",
            minWidth: 140,
        },
        {
            field: "is_automated_import",
            headerName: "Import Type",
            valueGetter: (params) => (params.data?.is_automated_import ? "Automated" : "Manual"),
            minWidth: 130,
        },
		{headerName: "Actions", colId: "actions", cellRenderer: ActionsButton, sortable: false, resizable: false, minWidth: 100,},
	]

	const domainColumns: CSVColumn<any>[] = [
		{ header: "ID", accessor: "id" },
		{ header: "Domain", accessor: "domain" },
		{ header: "User ID", accessor: "user_id" },
		{ header: "User Email", accessor: "user_email" },
		{ header: "Workspace ID", accessor: "workspace_id" },
		{ header: "Workspace Name", accessor: "workspace_name" },
		{ header: "Status", accessor: "status" },
		{ header: "Setup Complete", accessor: (row) => (row.setup_complete ? "Yes" : "No") },
		{ header: "Setup Stage", accessor: "current_setup_stage" },
		{ header: "Blacklist Status", accessor: "blacklist_status" },
		{ header: "NS Status", accessor: (row) => row.ns_status || "N/A" },
		{ 
			header: "Domain Registrar Name", 
			accessor: (row) => (row.registrar_name ? row.registrar_name : "Manual") 
		},
		{ header: "Registrar Icon Name", accessor: "registrar_icon_name" },
		{ 
			header: "Import Type", 
			accessor: (row) => (row.is_automated_import ? "Automated" : "Manual") 
		},
		{ 
			header: "Last NS Check At", 
			accessor: (row) => (row.last_ns_check_at ? new Date(row.last_ns_check_at).toLocaleString() : "N/A") 
		},
		{ 
			header: "NS Mismatch Detected At", 
			accessor: (row) => (row.ns_mismatch_detected_at ? new Date(row.ns_mismatch_detected_at).toLocaleString() : "N/A") 
		},
		{ header: "Contacts Count", accessor: "contacts_count" },
		{ 
			header: "Active Campaigns", 
			accessor: (row) => (Array.isArray(row.active_campaigns) ? row.active_campaigns.join("; ") : "") 
		},
		{ 
			header: "Email Subdomains", 
			accessor: (row) => 
				Array.isArray(row.subdomains) 
					? row.subdomains.map((s: any) => s.subdomain).join("; ") 
					: "" 
		},
		{ header: "Naming Strategy", accessor: "naming_strategy" },
		{ header: "AWS Hosted Zone ID", accessor: "aws_hosted_zone_id" },
		{ header: "Seconds Remaining", accessor: "seconds_remaining" },
		{ header: "Redirect Subdomains To", accessor: "redirect_subdomains_to" },
		{ header: "Warmup Progress (%)", accessor: "warmup_progress_percent" },
		{ header: "Warmup Hours Remaining", accessor: "warmup_hours_remaining" },
		{ header: "Warmup Days Remaining", accessor: "warmup_days_remaining" },
		{ 
			header: "Created On", 
			accessor: (row) => (row.created_on_ts ? new Date(row.created_on_ts).toLocaleString() : "N/A") 
		},
	];

	const ExportUserConnectedDomainsCsvButton = () => {

		const handleExportDomains = async () => {
			setExportLoading(true);

			try {
				const response = await authenticateAndFetchData(
					`/admin/get-user-connected-domains/?user_id=${userId}&export_all=true`
				);
				
				const domainsToExport = response?.data?.data || [];

				if (!domainsToExport.length) {
					enqueueSnackbar("No connected domains available to export.", { variant: "error" });
					return;
				}

				const success = exportToCSV(
					domainsToExport,
					domainColumns,					
					getExportFilename(user?.email ?? "unknown", "ConnectedDomains")
				);

				if (success) {
					enqueueSnackbar("Connected domains exported successfully!", { variant: "success" });
				} else {
					enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
				}
			} catch (err) {
				console.error("Export error:", err);
				enqueueSnackbar("Error while exporting connected domains.", { variant: "error" });
			} finally {
				setExportLoading(false);
			}
		};

		return (
			<Button
				startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={handleExportDomains}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		);
	};

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && adminUserConnectedDomainsQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (adminUserConnectedDomainsQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={adminUserConnectedDomainsQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton onClick={() => navigate(-1)}><ArrowBack/></IconButton>
					<Typography fontWeight={"bold"} variant={"h5"}>Connected Domains ({pageData.user_email})</Typography>
				</Stack>
				<Divider/>
				<Typography variant={"h5"}>
					⚠️ IMPORTANT: Please press delete button for each domain only ONCE.
					It can take around 30-60 seconds for some domains to get deleted. Refresh the page every few seconds to check.
				</Typography>
				<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={
							adminUserConnectedDomainsQuery.isFetching ||
							fetchWarmupDataMutation.isPending
						}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search domain, workspace or status..."
						getRowHeight={(params) => {
							const value = params?.data?.blacklist_status;
							if (!value || value === "Healthy" || value === "Unknown Error") {
								return 42;
							}
							const sources =
								typeof value === "string" ? value.split(",") : [];
							const visibleChips = Math.min(sources.length, 4);
							return visibleChips * 32 + 8;
						}}
						columnLabels={columnCustomizerLabel}
						actions={<ExportUserConnectedDomainsCsvButton />}
					/>
				</Box>
				<SubdomainsDialog />
				<NameserverDialog />
				{/*<EditWarmupLimit open={openEditWarmup}*/}
				{/*								 warmupDatetime={editWarmupDateTime}*/}
				{/*								 onClose={() => setOpenEditWarmup(false)}*/}
				{/*								 onSuccess={() => {*/}
				{/*										 setOpenEditWarmup(false);*/}
				{/*										 adminUserConnectedDomainsQuery.refetch().then();*/}
				{/*									 }}/>*/}
			</Stack>
		)
	} else {
		// Ideally it should not reach here.
		return <></>
	}
}


// function EditWarmupLimit(props: {
// 	open: boolean;
// 	onClose: () => void;
// 	onSuccess: () => void;
// }) {
// 	const {enqueueSnackbar} = useSnackbar();
//
// 	useEffect(() => {
// 		if (props.warmupDatetime?.seconds_remaining != null) {
// 			setNewDate(
// 				DateTime.now().plus({seconds: Number(props.warmupDatetime.seconds_remaining)})
// 			);
// 		}
// 	}, [props.open, props.warmupDatetime]);
//
// 	const WarmupDateTimeMutation = useMutation({
// 		mutationKey: ["WarmupDateTime"],
// 		mutationFn: () =>
// 			authenticateAndPostData("/admin/admin-update-warmup-date/", {
// 				id: props.warmupDatetime.id,
// 				warmup_datetime: newDate ? newDate.toISO() : null,
// 			}),
// 		gcTime: 0,
// 		retry: retryFn,
// 		onSuccess: () => {
// 			props.onSuccess();
// 			enqueueSnackbar("Campaign start time updated successfully!", {
// 				variant: "success",
// 			});
// 		},
// 		onError: (error: ApiRequestFailed) => {
// 			enqueueSnackbar(error.data.message, {
// 				variant: "error",
// 			});
// 			console.error(error);
// 		},
// 	});
//
// 	const handleSave = async () => {
// 		if (!newDate) return;
//
// 		if (newDate < DateTime.now()) {
// 			enqueueSnackbar("Please select a future date & time.", {variant: "error"});
// 			return;
// 		}
//
// 		WarmupDateTimeMutation.mutate();
// 	};
//
//
// 	return (
// 		<LocalizationProvider dateAdapter={AdapterLuxon}>
// 			<Dialog onClose={props.onClose} open={props.open} maxWidth="sm" fullWidth>
// 				<DialogTitle>Update Warmup Date Time</DialogTitle>
// 				<DialogContent>
// 					<DateTimePicker
// 						slotProps={{
// 							textField: {
// 								placeholder: "MM/DD/YYYY hh:mm a",
// 								label: "",
// 								InputLabelProps: {shrink: false},
// 							},
// 						}}
// 						value={newDate}
// 						onChange={(val) => setNewDate(val)}
// 						minDateTime={DateTime.now()}
// 					/>
// 				</DialogContent>
// 				<DialogActions sx={{px: 3, pb: 2}}>
// 					<Button onClick={props.onClose} disabled={WarmupDateTimeMutation.isPending}>
// 						Cancel
// 					</Button>
// 					<Button onClick={handleSave} variant="contained" disabled={!newDate}>
// 						Save
// 					</Button>
// 				</DialogActions>
// 			</Dialog>
// 		</LocalizationProvider>
// 	);
// }

function DomainRedirectionDialog(props: {
	managedSubdomain: string,
	redirect_to: string | null,
	disabled: boolean,
	workspaceId: number
}) {
	const {enqueueSnackbar} = useSnackbar();
	const navigate = useNavigate();

	const [open, setOpen] = useState(false);
	const [domain, setDomain] = useState<string>(
		props.redirect_to && !/^https:\/\//i.test(props.redirect_to)
			? `https://${props.redirect_to}`
			: props.redirect_to || ""
	);

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const setupMutation = useMutation({
		mutationKey: ["setupMutation"],
		mutationFn: () => authenticateAndPostData("/admin-domain-redirect/setup/", {
			"workspace_id": props.workspaceId,
			"managed_domain": props.managedSubdomain,
			"target_domain": domain,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			navigate(0);
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const deleteMutation = useMutation({
		mutationKey: ["deleteMutation"],
		mutationFn: () => authenticateAndPostData("/admin-domain-redirect/delete/", {
			"workspace_id": props.workspaceId,
			"managed_domain": props.managedSubdomain,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			navigate(0);
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	return (
		<>
			<IconButton disabled={props.disabled} size={"small"} color={"primary"} onClick={handleClickOpen}>
				<Edit/>
			</IconButton>
			<Dialog
				open={open}
				onClose={handleClose}
			>
				<DialogTitle>Set Up Domain Redirection</DialogTitle>
				<DialogContent>
					<DialogContentText>
						By setting up domain redirection, you can redirect visiting web traffic for all email
						subdomains created under <b>{props.managedSubdomain}</b> to some other HTTPS URL.
						<br/><br/>
						Where should we redirect them to?
					</DialogContentText>
					<TextField
						autoFocus
						required
						type="url"
						label="Enter target URL (ex. https://example.com/page)"
						value={domain}
						onChange={e => setDomain(e.target.value)}
						variant="outlined"
						sx={{mt: 2}}
						fullWidth
					/>
					<Alert severity={"info"} sx={{mt: 2}}>
						It might take a few minutes for changes to be reflected due to DNS caching.
					</Alert>
					<Alert severity={"info"} sx={{mt: 2}}>
						Make sure the target site/domain is running on <b>HTTPS</b>.
					</Alert>
				</DialogContent>
				<DialogActions sx={{justifyContnt: "space-between"}}>
					{props.redirect_to &&
              <Button color={"error"}
                      disabled={setupMutation.isPending || deleteMutation.isPending}
                      onClick={() => {
												deleteMutation.mutate();
											}}>
                  Delete
              </Button>}
					<Button onClick={handleClose}
									disabled={setupMutation.isPending || deleteMutation.isPending}>
						Cancel
					</Button>
					<Button disabled={!domain.trim() || setupMutation.isPending || deleteMutation.isPending}
									onClick={() => {
										setupMutation.mutate();
									}}>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
